import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { showBuzzToast } from './NotificationContext';

const ChatContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const ChatProvider = ({ children }) => {
    const { user } = useAuth();
    const { socket, connected } = useSocket();
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    // unreadCount derivado de conversations - siempre sincronizado
    const unreadCount = useMemo(() =>
        conversations.filter(c => c.unread_count > 0).length,
        [conversations]
    );
    const [typingByConv, setTypingByConv] = useState({}); // { [conv_id]: { [user_name]: true } }
    const [onlineUsers, setOnlineUsers] = useState(new Set());
    const [replyingTo, setReplyingTo] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [userPresence, setUserPresence] = useState({});
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const [buzzActive, setBuzzActive] = useState(null); // { sender_name, conversation_id }
    const [buzzCooldownEnd, setBuzzCooldownEnd] = useState(0);
    const [buzzCooldownRemaining, setBuzzCooldownRemaining] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const activeConversationRef = useRef(null);
    const fetchConversationsRef = useRef(null);
    // Mantener refs sincronizados con el state
    useEffect(() => {
        activeConversationRef.current = activeConversation;
    }, [activeConversation]);

    const appendMessageIfMissing = useCallback((incomingMessage) => {
        if (!incomingMessage?.id) return;

        setMessages(prev => {
            if (prev.some(message => message.id === incomingMessage.id)) {
                return prev;
            }

            const updated = [...prev, incomingMessage];
            return updated.length > 500 ? updated.slice(-500) : updated;
        });
    }, []);

    const syncConversationFromMessage = useCallback((message, options = {}) => {
        if (!message?.conversation_id) return;

        const { unreadDelta = 0, unreadCount } = options;

        setConversations(prev => {
            const exists = prev.some(conv => conv.id === message.conversation_id);

            if (!exists) {
                fetchConversationsRef.current?.();
                return prev;
            }

            return prev
                .map(conv => {
                    if (conv.id !== message.conversation_id) {
                        return conv;
                    }

                    const nextUnreadCount = unreadCount ?? Math.max(0, (conv.unread_count || 0) + unreadDelta);

                    return {
                        ...conv,
                        unread_count: nextUnreadCount,
                        last_message: message,
                        updated_at: message.created_at
                    };
                })
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        });
    }, []);

    // Registrar event listeners del chat en el socket compartido
    useEffect(() => {
        if (!socket || !connected) return;

        const onNewMessage = (message) => {
            // Solo agregar al array de mensajes si pertenece a la conversación activa
            if (activeConversationRef.current?.id === message.conversation_id) {
                appendMessageIfMissing(message);
            }
            syncConversationFromMessage(message);
        };

        const onMessageNotification = (data) => {
            if (!data?.message) {
                return;
            }

            // Fallback: si la sala se perdió tras una reconexión, usar la notificación directa
            // para no depender de un F5 manual.
            if (activeConversationRef.current?.id === data.conversation_id) {
                appendMessageIfMissing(data.message);
                syncConversationFromMessage(data.message, { unreadCount: 0 });
                socket.emit('mark_read', { conversation_id: data.conversation_id });
                return;
            }

            syncConversationFromMessage(data.message, { unreadDelta: 1 });
        };

        const onUserTyping = ({ user_name, is_typing, conversation_id }) => {
            if (!conversation_id) return;
            setTypingByConv(prev => {
                const convTyping = { ...(prev[conversation_id] || {}) };
                if (is_typing) {
                    convTyping[user_name] = true;
                } else {
                    delete convTyping[user_name];
                }
                if (Object.keys(convTyping).length === 0) {
                    const next = { ...prev };
                    delete next[conversation_id];
                    return next;
                }
                return { ...prev, [conversation_id]: convTyping };
            });
        };

        const onUserOnline = ({ user_id }) => {
            setOnlineUsers(prev => new Set([...prev, user_id]));
        };

        const onUserOffline = ({ user_id }) => {
            setOnlineUsers(prev => {
                const updated = new Set(prev);
                updated.delete(user_id);
                return updated;
            });
        };

        const onOnlineUsers = ({ user_ids }) => {
            setOnlineUsers(prev => new Set([...prev, ...user_ids]));
        };

        const onMessageDeleted = ({ message_id, conversation_id }) => {
            setMessages(prev => prev.map(msg =>
                msg.id === message_id
                    ? { ...msg, is_deleted: true, content: null, file: null }
                    : msg
            ));
            setConversations(prev => prev.map(conv => {
                if (conv.id === conversation_id && conv.last_message?.id === message_id) {
                    return { ...conv, last_message: { ...conv.last_message, is_deleted: true, content: null } };
                }
                return conv;
            }));
        };

        const onReactionUpdated = ({ message_id, reactions }) => {
            setMessages(prev => prev.map(msg =>
                msg.id === message_id ? { ...msg, reactions } : msg
            ));
        };

        const onMessagePinned = ({ message_id, is_pinned }) => {
            setMessages(prev => prev.map(msg =>
                msg.id === message_id ? { ...msg, is_pinned } : msg
            ));
            // Actualizar lista de fijados
            if (is_pinned) {
                setMessages(prev => {
                    const pinned = prev.find(m => m.id === message_id);
                    if (pinned) {
                        setPinnedMessages(pp => [pinned, ...pp.filter(p => p.id !== message_id)]);
                    }
                    return prev;
                });
            } else {
                setPinnedMessages(prev => prev.filter(p => p.id !== message_id));
            }
        };

        const onMessageEdited = ({ message_id, content, edited_at }) => {
            setMessages(prev => prev.map(msg =>
                msg.id === message_id ? { ...msg, content, edited_at } : msg
            ));
            // Actualizar last_message en sidebar si aplica
            setConversations(prev => prev.map(conv => {
                if (conv.last_message?.id === message_id) {
                    return { ...conv, last_message: { ...conv.last_message, content, edited_at } };
                }
                return conv;
            }));
        };

        const onPollUpdated = ({ message_id, poll }) => {
            setMessages(prev => prev.map(msg =>
                msg.id === message_id ? { ...msg, poll } : msg
            ));
        };

        const onUserPresenceChanged = ({ user_id: uid, status }) => {
            setOnlineUsers(prev => {
                const updated = new Set(prev);
                updated.add(uid); // still online
                return updated;
            });
            setUserPresence(prev => ({ ...prev, [uid]: status }));
        };

        const onBuzzReceived = ({ conversation_id, sender_name }) => {
            // Reproducir sonido original de nudge MSN (siempre)
            try {
                const audio = new Audio('/sounds/nudge.mp3');
                audio.volume = 0.5;
                audio.play().catch(() => {});
            } catch (e) { /* Audio no disponible */ }

            const isOnThisConversation = activeConversationRef.current?.id === conversation_id;

            if (isOnThisConversation && document.visibilityState === 'visible') {
                // Está viendo esta conversación → shake + overlay
                setBuzzActive({ sender_name, conversation_id });
                setTimeout(() => setBuzzActive(null), 2500);
            } else if (document.visibilityState === 'visible') {
                // Está en Centro Sura pero no en esta conversación → toast interno
                showBuzzToast(sender_name);
            } else {
                // Tab en segundo plano → notificación del browser + título parpadeante
                if (Notification.permission === 'granted') {
                    new Notification('¡Zumbido!', {
                        body: `${sender_name} te ha enviado un zumbido`,
                        icon: '/favicon-100x100.png',
                        tag: 'buzz',
                    });
                } else if (Notification.permission === 'default') {
                    Notification.requestPermission();
                }

                const originalTitle = document.title;
                let flash = true;
                const titleInterval = setInterval(() => {
                    document.title = flash ? `🔔 ¡Zumbido de ${sender_name}!` : originalTitle;
                    flash = !flash;
                }, 500);

                const stopFlash = () => {
                    clearInterval(titleInterval);
                    document.title = originalTitle;
                    document.removeEventListener('visibilitychange', stopFlash);
                };
                document.addEventListener('visibilitychange', stopFlash);
                setTimeout(stopFlash, 10000);
            }
        };

        const onMessagesRead = ({ conversation_id, read_by }) => {
            if (read_by === user?.id) return;
            setMessages(prev => prev.map(msg =>
                msg.conversation_id === conversation_id && msg.sender_id === user?.id && !msg.is_read
                    ? { ...msg, is_read: true }
                    : msg
            ));
            setConversations(prev => prev.map(conv =>
                conv.id === conversation_id && conv.last_message?.sender_id === user?.id
                    ? { ...conv, last_message: { ...conv.last_message, is_read: true } }
                    : conv
            ));
        };

        socket.on('new_message', onNewMessage);
        socket.on('message_notification', onMessageNotification);
        socket.on('user_typing', onUserTyping);
        socket.on('user_online', onUserOnline);
        socket.on('user_offline', onUserOffline);
        socket.on('online_users', onOnlineUsers);
        socket.on('message_deleted', onMessageDeleted);
        socket.on('reaction_updated', onReactionUpdated);
        socket.on('message_pinned', onMessagePinned);
        socket.on('message_edited', onMessageEdited);
        socket.on('poll_updated', onPollUpdated);
        socket.on('user_presence_changed', onUserPresenceChanged);
        const onChatReminder = (data) => {
            // Dispatch custom event que ChatView puede escuchar
            window.dispatchEvent(new CustomEvent('chat_reminder', { detail: data }));
        };

        socket.on('buzz_received', onBuzzReceived);
        socket.on('messages_read', onMessagesRead);
        socket.on('chat_reminder', onChatReminder);

        return () => {
            socket.off('new_message', onNewMessage);
            socket.off('message_notification', onMessageNotification);
            socket.off('user_typing', onUserTyping);
            socket.off('user_online', onUserOnline);
            socket.off('user_offline', onUserOffline);
            socket.off('online_users', onOnlineUsers);
            socket.off('message_deleted', onMessageDeleted);
            socket.off('reaction_updated', onReactionUpdated);
            socket.off('message_pinned', onMessagePinned);
            socket.off('message_edited', onMessageEdited);
            socket.off('poll_updated', onPollUpdated);
            socket.off('user_presence_changed', onUserPresenceChanged);
            socket.off('buzz_received', onBuzzReceived);
            socket.off('messages_read', onMessagesRead);
            socket.off('chat_reminder', onChatReminder);
        };
    }, [socket, connected, appendMessageIfMissing, syncConversationFromMessage]);

    // Cargar conversaciones
    const fetchConversations = useCallback(async () => {
        if (!user?.id) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/conversations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setConversations(data);
            }
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    }, [user?.id]);

    // Mantener ref de fetchConversations sincronizado
    fetchConversationsRef.current = fetchConversations;

    // Cargar mensajes de una conversación
    const fetchMessages = useCallback(async (conversationId, searchQuery = '') => {
        if (!conversationId) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let url = `${API_URL}/chat/conversations/${conversationId}/messages?per_page=50`;
            if (searchQuery) {
                url += `&search=${encodeURIComponent(searchQuery)}`;
            }
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setMessages(data.messages);
                setHasMore(data.has_more);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Cargar mensajes anteriores (paginación)
    const loadMoreMessages = useCallback(async () => {
        if (!hasMore || loadingMore || !activeConversation) return;

        try {
            setLoadingMore(true);
            const nextPage = currentPage + 1;
            const token = localStorage.getItem('token');
            const url = `${API_URL}/chat/conversations/${activeConversation.id}/messages?page=${nextPage}&per_page=50`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setMessages(prev => [...data.messages, ...prev]);
                setHasMore(data.has_more);
                setCurrentPage(nextPage);
            }
        } catch (error) {
            console.error('Error loading more messages:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [hasMore, loadingMore, activeConversation, currentPage]);

    // Buscar mensajes en la conversación activa
    const searchMessages = useCallback(async (query) => {
        if (!activeConversation) return;
        await fetchMessages(activeConversation.id, query);
    }, [activeConversation, fetchMessages]);

    // Obtener mensajes fijados
    const fetchPinnedMessages = useCallback(async (conversationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/conversations/${conversationId}/pinned`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPinnedMessages(data);
                return data;
            }
        } catch (error) {
            console.error('Error fetching pinned messages:', error);
        }
        return [];
    }, []);

    // Unirse a una conversación
    const joinConversation = useCallback(async (conversationId) => {
        if (socket && conversationId) {
            // Salir de la anterior si existe
            if (activeConversation) {
                socket.emit('leave_conversation', { conversation_id: activeConversation.id });
            }

            socket.emit('join_conversation', { conversation_id: conversationId });
            let conv = conversations.find(c => c.id === conversationId);

            // Si la conversación no está en la lista (recién creada), refetchear
            if (!conv) {
                await fetchConversations();
                conv = conversations.find(c => c.id === conversationId);
            }

            setActiveConversation(conv || { id: conversationId });
            setMessages([]);
            setPinnedMessages([]);
            setReplyingTo(null);
            setEditingMessage(null);
            fetchMessages(conversationId);
            fetchPinnedMessages(conversationId);

            // Marcar como leído
            socket.emit('mark_read', { conversation_id: conversationId });
            setConversations(prev => prev.map(c =>
                c.id === conversationId ? { ...c, unread_count: 0 } : c
            ));
        }
    }, [socket, activeConversation, conversations, fetchConversations, fetchMessages, fetchPinnedMessages]);

    // Enviar mensaje (usa ref para evitar closures obsoletos durante uploads async)
    const sendMessage = useCallback((content, fileId = null, replyToId = null) => {
        const conv = activeConversationRef.current;
        if (socket && conv && (content?.trim() || fileId)) {
            socket.emit('send_message', {
                conversation_id: conv.id,
                content: content?.trim(),
                file_id: fileId,
                reply_to_id: replyToId
            });
            setReplyingTo(null);
        }
    }, [socket]);

    // Indicador de escritura
    const sendTyping = useCallback((isTyping) => {
        if (socket && activeConversation && user) {
            const userName = user.empleado?.nombre_display || user.username;
            socket.emit('typing', {
                conversation_id: activeConversation.id,
                user_name: userName,
                is_typing: isTyping
            });
        }
    }, [socket, activeConversation, user]);

    // Crear nueva conversación
    const createConversation = useCallback(async (participantIds, name = null, isGroup = false) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/conversations`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    participant_ids: participantIds,
                    name,
                    is_group: isGroup
                })
            });

            if (response.ok) {
                const newConv = await response.json();
                setConversations(prev => [newConv, ...prev]);
                return newConv;
            }
        } catch (error) {
            console.error('Error creating conversation:', error);
        }
        return null;
    }, []);

    // Crear grupo (wrapper conveniente)
    const createGroup = useCallback(async (name, participantIds) => {
        return await createConversation(participantIds, name, true);
    }, [createConversation]);

    // Obtener detalles del grupo
    const getGroupDetails = useCallback(async (groupId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/groups/${groupId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.error('Error fetching group details:', error);
        }
        return null;
    }, []);

    // Invitar usuarios al grupo (solo owner)
    const inviteToGroup = useCallback(async (groupId, userIds) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/groups/${groupId}/invite`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ user_ids: userIds })
            });

            if (response.ok) {
                const result = await response.json();
                // Refrescar conversaciones para actualizar participantes
                await fetchConversations();
                return result;
            } else {
                const error = await response.json();
                return { error: error.error || 'Error al invitar usuarios' };
            }
        } catch (error) {
            console.error('Error inviting to group:', error);
            return { error: 'Error de conexión' };
        }
    }, [fetchConversations]);

    // Remover usuario del grupo (solo owner)
    const removeFromGroup = useCallback(async (groupId, userId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/groups/${groupId}/members/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const result = await response.json();
                // Refrescar conversaciones
                await fetchConversations();
                return result;
            } else {
                const error = await response.json();
                return { error: error.error || 'Error al remover usuario' };
            }
        } catch (error) {
            console.error('Error removing from group:', error);
            return { error: 'Error de conexión' };
        }
    }, [fetchConversations]);

    // Transferir administración del grupo
    const transferAdmin = useCallback(async (groupId, userId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/groups/${groupId}/transfer-admin`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ user_id: userId })
            });

            if (response.ok) {
                const result = await response.json();
                await fetchConversations();
                return result;
            } else {
                const error = await response.json();
                return { error: error.error || 'Error al transferir administración' };
            }
        } catch (error) {
            console.error('Error transferring admin:', error);
            return { error: 'Error de conexión' };
        }
    }, [fetchConversations]);

    // Salir del grupo (para miembros, no owner)
    const leaveGroup = useCallback(async (groupId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/groups/${groupId}/leave`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                // Remover de la lista local
                setConversations(prev => prev.filter(c => c.id !== groupId));
                if (activeConversation?.id === groupId) {
                    setActiveConversation(null);
                    setMessages([]);
                }
                return await response.json();
            } else {
                const error = await response.json();
                return { error: error.error || 'Error al salir del grupo' };
            }
        } catch (error) {
            console.error('Error leaving group:', error);
            return { error: 'Error de conexión' };
        }
    }, [activeConversation]);

    // Subir archivo
    const uploadFile = useCallback(async (file) => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/chat/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.error('Error uploading file:', error);
        }
        return null;
    }, []);

    // Eliminar mensaje
    const deleteMessage = useCallback((messageId) => {
        if (!socket) return;
        socket.emit('delete_message', { message_id: messageId });
    }, [socket]);

    // Toggle reacción
    const toggleReaction = useCallback((messageId, emoji) => {
        if (!socket) return;
        socket.emit('toggle_reaction', { message_id: messageId, emoji });
    }, [socket]);

    // Toggle pin
    const togglePin = useCallback((messageId) => {
        if (!socket) return;
        socket.emit('toggle_pin', { message_id: messageId });
    }, [socket]);

    // Editar mensaje
    const editMessage = useCallback((messageId, content) => {
        if (!socket) return;
        socket.emit('edit_message', { message_id: messageId, content });
        setEditingMessage(null);
    }, [socket]);

    // Reenviar mensaje
    const forwardMessage = useCallback((messageId, targetConversationId) => {
        if (!socket) return;
        socket.emit('forward_message', { message_id: messageId, target_conversation_id: targetConversationId });
    }, [socket]);

    // Crear encuesta
    const createPoll = useCallback((question, options) => {
        const conv = activeConversationRef.current;
        if (!socket || !conv) return;
        socket.emit('create_poll', {
            conversation_id: conv.id,
            question,
            options
        });
    }, [socket]);

    // Votar en encuesta
    const votePoll = useCallback((pollId, optionId) => {
        if (!socket) return;
        socket.emit('vote_poll', { poll_id: pollId, option_id: optionId });
    }, [socket]);

    // Cambiar estado de presencia
    const setPresenceStatus = useCallback((status) => {
        if (!socket) return;
        socket.emit('set_presence', { status });
    }, [socket]);

    // Enviar zumbido (cooldown global de 30s)
    const sendBuzz = useCallback(() => {
        const conv = activeConversationRef.current;
        if (!socket || !conv || buzzCooldownEnd > Date.now()) return false;
        socket.emit('send_buzz', { conversation_id: conv.id });
        setBuzzCooldownEnd(Date.now() + 30000);
        return true;
    }, [socket]);

    // ==================== BOOKMARKS ====================
    const fetchBookmarks = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/bookmarks`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.error('Error fetching bookmarks:', error);
        }
        return [];
    }, []);

    const addBookmark = useCallback(async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/bookmarks`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ message_id: messageId })
            });
            if (response.ok) {
                return await response.json();
            }
            if (response.status === 409) {
                return { already_exists: true };
            }
        } catch (error) {
            console.error('Error adding bookmark:', error);
        }
        return null;
    }, []);

    const removeBookmark = useCallback(async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/bookmarks/${messageId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.ok;
        } catch (error) {
            console.error('Error removing bookmark:', error);
        }
        return false;
    }, []);

    const addBookmarkNote = useCallback(async ({ content, file_id } = {}) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/bookmarks/notes`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ content, file_id })
            });
            if (response.ok) return await response.json();
        } catch (error) {
            console.error('Error adding bookmark note:', error);
        }
        return null;
    }, []);

    const uploadChatFile = useCallback(async (file) => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${API_URL}/chat/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            if (response.ok) return await response.json();
        } catch (error) {
            console.error('Error uploading file:', error);
        }
        return null;
    }, []);

    const removeBookmarkNote = useCallback(async (bookmarkId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/bookmarks/notes/${bookmarkId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.ok;
        } catch (error) {
            console.error('Error removing bookmark note:', error);
        }
        return false;
    }, []);

    // ==================== REMINDERS ====================
    const createReminder = useCallback(async (messageId, remindAt) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/reminders`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ message_id: messageId, remind_at: remindAt })
            });
            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.error('Error creating reminder:', error);
        }
        return null;
    }, []);

    // Obtener usuarios disponibles
    const fetchAvailableUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/users`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
        return [];
    }, []);


    // Eliminar conversación
    const deleteConversation = useCallback(async (conversationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/conversations/${conversationId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setConversations(prev => prev.filter(c => c.id !== conversationId));
                if (activeConversation?.id === conversationId) {
                    setActiveConversation(null);
                    setMessages([]);
                }
                return true;
            }
        } catch (error) {
            console.error('Error deleting conversation:', error);
        }
        return false;
    }, [activeConversation]);

    // Cargar conversaciones al iniciar
    useEffect(() => {
        if (user?.id) {
            fetchConversations();
        }
    }, [user?.id, fetchConversations]);

    useEffect(() => {
        if (!connected) return;
        const activeConversationId = activeConversationRef.current?.id;

        fetchConversations();

        if (!activeConversationId) {
            return;
        }

        socket?.emit('join_conversation', { conversation_id: activeConversationId });
        socket?.emit('mark_read', { conversation_id: activeConversationId });
        fetchMessages(activeConversationId);
        fetchPinnedMessages(activeConversationId);
    }, [connected, socket, fetchConversations, fetchMessages, fetchPinnedMessages]);

    useEffect(() => {
        if (!user?.id) return;

        const recoverChatState = () => {
            const activeConversationId = activeConversationRef.current?.id;

            if (!socket) {
                fetchConversations();
                return;
            }

            if (!socket.connected) {
                socket.connect();
                return;
            }

            // Solo refrescar sidebar (conversaciones), no reemplazar mensajes
            // Los mensajes llegan en tiempo real via socket si la conexión está activa
            fetchConversations();

            if (activeConversationId) {
                socket.emit('mark_read', { conversation_id: activeConversationId });
                setConversations(prev => prev.map(c =>
                    c.id === activeConversationId ? { ...c, unread_count: 0 } : c
                ));
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                recoverChatState();
            }
        };

        window.addEventListener('focus', recoverChatState);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', recoverChatState);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user?.id, socket, fetchConversations]);

    // Ticker del cooldown del zumbido
    useEffect(() => {
        if (buzzCooldownEnd <= 0) return;

        const tick = () => {
            const remaining = Math.max(0, Math.ceil((buzzCooldownEnd - Date.now()) / 1000));
            setBuzzCooldownRemaining(remaining);
            if (remaining <= 0) {
                setBuzzCooldownEnd(0);
            }
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [buzzCooldownEnd]);

    const value = {
        socket,
        connected,
        conversations,
        activeConversation,
        messages,
        unreadCount,
        typingByConv,
        onlineUsers,
        loading,
        hasMore,
        loadingMore,
        fetchConversations,
        joinConversation,
        sendMessage,
        sendTyping,
        createConversation,
        createGroup,
        deleteConversation,
        uploadFile,
        deleteMessage,
        toggleReaction,
        togglePin,
        fetchPinnedMessages,
        pinnedMessages,
        replyingTo,
        setReplyingTo,
        editingMessage,
        setEditingMessage,
        editMessage,
        forwardMessage,
        createPoll,
        votePoll,
        userPresence,
        setPresenceStatus,
        fetchAvailableUsers,
        setActiveConversation,
        searchMessages,
        loadMoreMessages,
        // Group management
        getGroupDetails,
        inviteToGroup,
        removeFromGroup,
        leaveGroup,
        transferAdmin,
        // Zumbido
        sendBuzz,
        buzzActive,
        buzzCooldownRemaining,
        // Bookmarks
        fetchBookmarks,
        addBookmark,
        removeBookmark,
        addBookmarkNote,
        removeBookmarkNote,
        uploadChatFile,
        // Reminders
        createReminder
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat debe ser usado dentro de un ChatProvider');
    }
    return context;
};
